import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { connect } from 'react-redux'
import moment from 'moment'
import cloneDeep from 'lodash-es/cloneDeep'
import InfiniteScroll from 'react-infinite-scroll-component'

import { getTokenType } from 'utils/tokenTypes'
import Layout from '../../../components/common/Layout'
import NoContent from '../../../components/common/NoContent'
import Loading from '../../../components/common/Loading'
import Side from '../../../components/order/Side'
import Pagination from '../../../components/order/Pagination'
import OrderTable from '../../../components/order/OrderTable'
import Filter from '../../../components/order/OrderHistoryFilter'
import FontNumber from '../../../components/common/FontNumber'
import { FilledItem } from '../../../components/exchange/OpenOrders'
import TradeHistoryMobileItem from './tradeHistoryMobileItem'
import { MobileContent, Content } from '../../order/orderHistory'

import { getTradeHistoryByAddressQS } from '../../../redux/trade/action'

import httpRequest from '../../../utils/httpRequest'
import { roundValue } from '../../../utils/number'
import reduceTradePairBSufix from '../../../utils/tradepair'
import { withI18n } from '../../../i18n/util'
import { DexThemeProvider } from '../../../components/common/DexThemeProvider'
import ExtensionWalletProvider from '../../../components/ExtensionWalletProvider'

const PAGE_SIZE = 20

const Wrapper = styled.div`
  background: #fdfdfd;
  display: flex;
  justify-content: center;
`

const Title = styled.div`
  color: #212833;
  font-size: 20px;
  background: #fff;
  height: 40px;
  line-height: 40px;
`

const FilterWrap = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  background: #fff;
  box-shadow: 0px 1px 20px 0px rgba(90, 102, 124, 0.08);
  height: 40px;
  padding: 0 5px 0 15px;
`

const CellItem = styled.div`
  color: #212833;
`

const DateItem = styled.div`
  color: #848e9c;
`

const PagingWrap = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 15px;
`

class TradeHistory extends PureComponent {
  static propTypes = {
    tradeHistory: PropTypes.array,
    total: PropTypes.number,
    address: PropTypes.string,
    isFetchTradeHistory: PropTypes.bool,
    dispatch: PropTypes.func,
    formatI18nText: PropTypes.func,
  }

  static defaultProps = {
    tradeHistory: [],
    total: 0,
    address: '',
    isFetchTradeHistory: false,
    dispatch: () => {},
    formatI18nText: () => {},
  }

  constructor(props) {
    super(props)
    this.state = {
      trades: [],
      filterParams: {},
      offset: 0,
    }
  }

  componentDidMount() {
    const currentDate = new Date()
    const start = currentDate.setDate(currentDate.getDate() - 7)
    const end = new Date()
    const opts = {
      start: new Date(`${moment(start).format('YYYY/MM/DD')} 00:00:00`).getTime(),
      end: new Date(`${moment(end).format('YYYY/MM/DD')} 23:59:59`).getTime(),
    }
    this._getTradesHistory(opts)
  }

  componentDidUpdate() {
    const { trades } = this.state
    const { tradeHistory } = this.props
    if (trades.length === 0 && tradeHistory.length > 0) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ trades: trades.concat(tradeHistory) })
    }
  }

  _getTradesHistory = opts => {
    const tokenType = getTokenType()
    const { dispatch } = this.props
    const { filterParams } = this.state
    let user = sessionStorage.getItem('user')
    user = (user && JSON.parse(user)) || {}
    if (!user.address) return

    const params = {
      address: user.address,
      offset: 0,
      total: 1,
      limit: PAGE_SIZE,
      ...filterParams,
      ...opts,
    }

    dispatch(getTradeHistoryByAddressQS({ params, withLoading: true, tokenType }))
  }

  _paging = current => {
    this._getTradesHistory({
      offset: (current - 1) * PAGE_SIZE,
    })
  }

  _buildColumns = () => {
    const { formatI18nText } = this.props
    const columns = [
      {
        title: formatI18nText('exchange.blockHeight'),
        dataIndex: 'blockHeight',
        headerAlign: 'left',
        sorter: true,
        width: '10%',
        key: 'BlockHeight',
        render: item => (
          <CellItem>
            <FontNumber value={item.blockHeight} />
          </CellItem>
        ),
      },
      {
        title: formatI18nText('exchange.date'),
        dataIndex: 'date',
        headerAlign: 'left',
        sorter: true,
        width: '20%',
        key: 'Date',
        render: item => (
          <DateItem>
            <FontNumber value={moment(item.date).format('MM-DD HH:mm:ss')} />
          </DateItem>
        ),
      },
      {
        title: formatI18nText('exchange.pair'),
        dataIndex: 'pair',
        key: 'Pair',
        width: '10%',
        render: item => (
          <CellItem>{reduceTradePairBSufix(`${item.baseAsset}_${item.quoteAsset}`)}</CellItem>
        ),
      },
      {
        title: formatI18nText('exchange.side'),
        dataIndex: 'side',
        key: 'Side',
        width: '10%',
        render: item => <Side text={item.side} />,
      },
      {
        title: formatI18nText('exchange.price'),
        dataIndex: 'price',
        key: 'Price',
        width: '20%',
        render: item => (
          <CellItem>
            <FontNumber value={item.price} />
          </CellItem>
        ),
      },
      {
        title: formatI18nText('exchange.filled'),
        dataIndex: 'filled',
        key: 'Filled',
        width: '10%',
        render: item => (
          <FilledItem fill={true} style={{ textAlign: 'left' }}>
            <FontNumber value={item.filled} />
          </FilledItem>
        ),
      },
      {
        title: formatI18nText('exchange.total'),
        dataIndex: 'total',
        key: 'Total',
        width: '20%',
        render: item => (
          <CellItem>
            <FontNumber value={item.total} />
          </CellItem>
        ),
      },
    ]

    return columns
  }

  _buildDataSource = () => {
    const { tradeHistory } = this.props
    const result = []
    tradeHistory.forEach(trade => {
      result.push({
        blockHeight: trade.blockHeight,
        // date: moment(trade.time).utc(8).format(),
        date: trade.time,
        pair: '',
        quoteAsset: trade.quoteAsset,
        baseAsset: trade.baseAsset && trade.baseAsset.replace(/-.+/, ''),
        side: this._buildSide(trade),
        price: roundValue(trade.price),
        filled: roundValue(trade.quantity),
        total: roundValue(trade.price * trade.quantity),
      })
    })
    return result
  }

  _buildSide = trade => {
    if (trade.buyerId === trade.sellerId) return 'Self'

    const { address } = this.props
    return trade.buyerId === address ? 'Buy' : 'Sell'
  }

  _filter = params => {
    const opts = {}

    if (params.manualStartDate) {
      opts.start = params.manualStartDate.getTime()
    }

    if (params.manualEndDate) {
      opts.start = new Date(
        `${moment(params.manualStartDate).format('YYYY/MM/DD')} 00:00:00`
      ).getTime()
      opts.end = new Date(`${moment(params.manualEndDate).format('YYYY/MM/DD')} 23:59:59`).getTime()
    }

    if (params.side) {
      opts.side = params.side
    }

    if (params.baseAsset && params.quoteAsset) {
      opts.symbol = `${params.baseAsset}_${params.quoteAsset}`
    }

    this.setState({ filterParams: opts }, () => {
      this._getTradesHistory(opts)
    })
  }

  _buildMobileDataSource = () => {
    const { trades } = this.state
    const result = []
    trades.forEach(trade => {
      result.push({
        blockHeight: trade.blockHeight,
        date: moment(trade.time)
          .utc(8)
          .format(),
        pair: '',
        quoteAsset: trade.quoteAsset,
        baseAsset: trade.baseAsset && trade.baseAsset.replace(/-.+/, ''),
        side: this._buildSide(trade),
        price: roundValue(trade.price),
        filled: roundValue(trade.quantity),
        total: roundValue(trade.price * trade.quantity),
      })
    })

    return result
  }

  _fetchMore = async () => {
    const { offset, trades } = this.state
    let user = sessionStorage.getItem('user')
    user = (user && JSON.parse(user)) || {}
    if (!user.address) return

    const params = {
      address: user.address,
      offset: offset + 1,
      total: 1,
      limit: PAGE_SIZE,
    }

    const tradeResponse = await httpRequest.getOrderTradeHistoryQS(params)

    this.setState({ offset: offset + 1, trades: trades.concat(tradeResponse.trade) })
  }

  render() {
    const columns = this._buildColumns()
    const dataSource = this._buildDataSource()
    const mobileDataSource = this._buildMobileDataSource()

    const { total, isFetchTradeHistory, formatI18nText } = this.props

    return (
      <DexThemeProvider theme="light">
        <Layout>
          <Wrapper>
            <Content>
              <Title>{formatI18nText('header.tradeHistory')}</Title>
              <FilterWrap>
                <Filter
                  hasType={true}
                  hasPair={true}
                  onFilterChange={this._filter}
                  reset={this._reset}
                />
              </FilterWrap>
              <OrderTable columns={columns} dataSource={dataSource} />
              {total > PAGE_SIZE && (
                <PagingWrap>
                  <Pagination totalCount={total} pageSize={PAGE_SIZE} paging={this._paging} />
                </PagingWrap>
              )}
              {dataSource.length === 0 && (
                <NoContent text={formatI18nText('tradeHistory.tradeHistory')} />
              )}
            </Content>
            <MobileContent>
              <Title>{formatI18nText('header.tradeHistory')}</Title>
              <InfiniteScroll
                dataLength={mobileDataSource.length}
                next={this._fetchMore}
                hasMore={total > mobileDataSource.length}
                loader={<h4>{formatI18nText('common.loading')}...</h4>}
              >
                {mobileDataSource.map(item => (
                  <TradeHistoryMobileItem trade={item} />
                ))}
              </InfiniteScroll>
            </MobileContent>
          </Wrapper>
          {isFetchTradeHistory && <Loading />}
        </Layout>
        <ExtensionWalletProvider />
      </DexThemeProvider>
    )
  }
}

const mapStateToProps = ({
  account: { address },
  trade: { accountTradeHistory, isFetchTradeHistory },
}) => ({
  tradeHistory: cloneDeep(accountTradeHistory.trade),
  isFetchTradeHistory,
  total: accountTradeHistory.total,
  address,
})

export default withI18n(connect(mapStateToProps)(TradeHistory))
